import React, { useContext, useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Main from "../Main/Main";
import Account from "../Account/Account";
import Auth from "../Auth/Auth";
import Login from "../Login/Login";
import NotFound from "../NotFound/NotFound";
import Header from "../Header/Header";
import Report from "../Report/Report";
import Approvals from "../Approvals/Approvals";
import Departments from "../Departments/Departments.js";
import FinancialReportTabs from "../FinancialReport/FinancialReportTabs.js";
import "./App.css";
import Stats from "../Stats/Stats";
import OrgChartNew from "../OrgChart/OrgChartNew";
import {
  UserProvider,
  UserContext,
} from "../../contexts/CurrentUserContext.js";
import AllNews from "../NewsGallery/AllNews/AllNews";
import { ReactNotifications } from "react-notifications-component";
import {
  PendingReportsProvider,
  usePendingReports,
} from "../../contexts/PendingReportsContext";
import {
  PendingTimesheetsProvider,
  usePendingTimesheets,
} from "../../contexts/PendingTimesheetsContext";
import {
  PendingSalaryApprovalsProvider,
  usePendingSalaryApprovals,
} from "../../contexts/PendingSalaryApprovalsContext";
import {
  PendingSketchDesignApprovalsProvider,
  usePendingSketchDesignApprovals,
} from "../../contexts/PendingSketchDesignApprovalsContext";
import {
  PendingPaymentsProgressApprovalsProvider,
  usePendingPaymentsProgressApprovals,
} from "../../contexts/PendingPaymentsProgressApprovalsContext";
import MainCalendarV2 from "../Main/MainCalendarV2/MainCalendarV2";
import VacationSchedule from "../Main/MainCalendarV2/components/VacationSchedule/VacationSchedule";
import ProjectsList from "../ProjectsV2/ProjectsList/ProjectsList";
import NewProject from "../ProjectsV2/NewProject/NewProject";
import EditProject from "../ProjectsV2/EditProject/EditProject";
import AddProjectTeam from "../ProjectsV2/AddProjectTeam/AddProjectTeam";
import EditProjectSelect from "../ProjectsV2/EditProject/EditProjectSelect";
import AddProjectTeamSelect from "../ProjectsV2/AddProjectTeam/AddProjectTeamSelect";
import ProjectFinance from "../ProjectsV2/ProjectFinance/ProjectFinance";
import ProjectFinanceSelect from "../ProjectsV2/ProjectFinance/ProjectFinanceSelect";
import LaborAnalysisSelect from "../ProjectsV2/LaborAnalysis/LaborAnalysisSelect";
import LaborAnalysis from "../ProjectsV2/LaborAnalysis/LaborAnalysis";
import ProjectFinanceSketchDesign from "../ProjectsV2/SketchDesignProjectFinance/ProjectFinanceSketchDesign";
import EpAccumulatedDeviations from "../ProjectsV2/SketchDesignProjectFinance/EpAccumulatedDeviations/EpAccumulatedDeviations";
import CounterpartiesDatabase from "../ProjectsV2/CounterpartiesDatabase/CounterpartiesDatabase";
import AddCounterparty from "../ProjectsV2/CounterpartiesDatabase/AddCounterparty/AddCounterparty";
import CounterpartyDetails from "../ProjectsV2/CounterpartiesDatabase/CounterpartyDetails/CounterpartyDetails";
import Badge from "../Report/Badge/Badge";
import PushNotificationTest from "../Notifications/PushNotificationTest";
import KnowledgeBase from "../KnowledgeBase/KnowledgeBase.tsx";
import KnowledgeBaseAdmin from "../KnowledgeBase/KnowledgeBaseAdmin";
import AddMaterial from "../KnowledgeBase/KnowledgeBaseAdmin/AddMaterial";
import TrainingList from "../KnowledgeBase/Training/TrainingList";
import TrainingLesson from "../KnowledgeBase/Training/TrainingLesson";
import OrganizationalTrainingTest from "../KnowledgeBase/Training/OrganizationalTrainingTest";
import OnboardingMaterials from "../OnboardingMaterials/OnboardingMaterials";
import {
  hasAccessToOvertimeApproval,
  hasAccessToTimesheetApproval,
  hasAccessToSalaryApproval,
  hasAccessToProjectApproval,
  hasAccessToEpApproval,
  hasAccessToApprovals,
  SKETCH_DESIGN_USER_IDS,
} from "../../utils/approvalsAccess";
import { getAgreementApprovalsByApprover } from "../../api/agreementsApi";
import { normalizeMyAgreementApprovalEntry } from "../Approvals/ProjectApproval/agreementApprovalHelpers";

function AppContent() {
  const [userName, setUserName] = useState("Loading...");
  const location = useLocation();
  const { userData } = useContext(UserContext);
  const { pendingOvertimesCount, pendingDayoffsCount, updatePendingCounts } =
    usePendingReports();
  const { pendingTimesheetsCount, updatePendingCount } = usePendingTimesheets();
  const { pendingSalaryApprovalsCount, updatePendingSalaryApprovalsCount } =
    usePendingSalaryApprovals();
  const {
    pendingSketchDesignApprovalsCount,
    updatePendingSketchDesignApprovalsCount,
  } = usePendingSketchDesignApprovals();
  const {
    pendingPaymentsProgressApprovalsCount,
    updatePendingPaymentsProgressApprovalsCount,
  } = usePendingPaymentsProgressApprovals();
  const [pendingAgreementApprovalsCount, setPendingAgreementApprovalsCount] =
    useState(0);
  const [totalPendingCount, setTotalPendingCount] = useState(0);
  const getLevelName = (level) =>
    typeof level === "string" ? level : level?.name;

  useEffect(() => {
    if (userData) {
      setUserName(`${userData.name} ${userData.surname}`.toUpperCase());
    }
  }, [userData]);

  useEffect(() => {
    if (userData && hasAccessToOvertimeApproval(userData)) {
      updatePendingCounts();
    }
  }, [userData, updatePendingCounts]);

  useEffect(() => {
    if (userData && hasAccessToTimesheetApproval(userData)) {
      updatePendingCount(userData.id);
    }
  }, [userData, updatePendingCount]);

  useEffect(() => {
    if (!userData) return;
    updatePendingSalaryApprovalsCount(userData);
  }, [userData, updatePendingSalaryApprovalsCount]);

  useEffect(() => {
    if (!userData || !hasAccessToEpApproval(userData)) {
      return;
    }

    updatePendingSketchDesignApprovalsCount(userData);
    updatePendingPaymentsProgressApprovalsCount(userData);
  }, [
    userData,
    updatePendingSketchDesignApprovalsCount,
    updatePendingPaymentsProgressApprovalsCount,
  ]);

  useEffect(() => {
    let cancelled = false;

    const updatePendingAgreementApprovals = async () => {
      if (!userData || !hasAccessToProjectApproval(userData)) {
        if (!cancelled) setPendingAgreementApprovalsCount(0);
        return;
      }
      try {
        const data = await getAgreementApprovalsByApprover(userData.id);
        if (cancelled) return;
        const list = (Array.isArray(data) ? data : [])
          .map((entry) => normalizeMyAgreementApprovalEntry(entry, userData.id))
          .filter(Boolean);
        const pendingCount = list.filter((item) => {
          const myStatus = item.myStep?.status ?? item.status;
          return myStatus === "pending";
        }).length;
        setPendingAgreementApprovalsCount(pendingCount);
      } catch {
        if (!cancelled) setPendingAgreementApprovalsCount(0);
      }
    };

    updatePendingAgreementApprovals();
    return () => {
      cancelled = true;
    };
  }, [userData]);

  useEffect(() => {
    const total =
      pendingOvertimesCount +
      pendingDayoffsCount +
      pendingTimesheetsCount +
      pendingSalaryApprovalsCount +
      pendingSketchDesignApprovalsCount +
      pendingPaymentsProgressApprovalsCount +
      pendingAgreementApprovalsCount;
    setTotalPendingCount(total);
  }, [
    pendingOvertimesCount,
    pendingDayoffsCount,
    pendingTimesheetsCount,
    pendingSalaryApprovalsCount,
    pendingSketchDesignApprovalsCount,
    pendingPaymentsProgressApprovalsCount,
    pendingAgreementApprovalsCount,
  ]);

  const hasFinancialReportAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some(
      (level) => ["АДМС", "ФИН"].includes(getLevelName(level))
    );

  const hasOrgChartAccess =
    userData &&
    (userData.access_levels?.some((level) =>
      ["АДМС", "ИК"].includes(getLevelName(level)),
    ) ||
      userData.id === 262);

  const hasAdminAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some(
      (level) => ["АДМС", "АДМП"].includes(getLevelName(level))
    );

  // Проверка доступа к базе знаний:
  // - на локальной и тестовой среде доступ у всех пользователей
  // - на прод среде доступ только у АДМС
  const isProdEnv = process.env.REACT_APP_ENV === "prod";
  const hasKnowledgeBaseAccess =
    userData &&
    (isProdEnv
      ? userData.access_levels &&
        userData.access_levels.some(
          (level) => ["АДМС", "АДМП"].includes(getLevelName(level))
        )
      : true);

  // Проверка доступа к администрированию пользователей (АДМС, ИК, ФИН, СИСТЕМАДМ)
  const hasUserAdminAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some((level) =>
      ["АДМС", "ИК", "ФИН", "СИСТЕМАДМ"].includes(getLevelName(level))
    );

  // Добавляем доступ к ProjectFinance для всех ролей, которые могут кликать на проекты
  // + пользователи ЭП с доступом к вкладкам ТЭП/КП/смета
  const hasProjectFinanceAccess =
    userData &&
    (SKETCH_DESIGN_USER_IDS.includes(userData.id) ||
      (userData.access_levels &&
        userData.access_levels.some(
          (level) =>
            ["АДМС", "АДМП", "ГИП", "РО", "ФИН"].includes(getLevelName(level))
        )));

  const hasPaymentsProgressAccess =
    userData &&
    (SKETCH_DESIGN_USER_IDS.includes(userData.id) ||
      userData.access_levels?.some((level) =>
        ["АДМС", "ФИН"].includes(getLevelName(level))
      ));

  // Анализ трудозатрат: АДМС, АДМП, РО, ФИН (без ГИП)
  const hasLaborAnalysisAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some((level) =>
      ["АДМС", "АДМП", "РО", "ФИН"].includes(getLevelName(level))
    );

  // Доступ к базе данных контрагентов только для АДМС
  const hasCounterpartiesAccess =
    userData &&
    userData.access_levels &&
    userData.access_levels.some((level) => getLevelName(level) === "АДМС");

  // Эскизный отдел (id 4) — страницы табеля для них нет, пункт меню скрываем
  const SKETCH_DESIGN_DEPARTMENT_ID = 4;
  const isSketchDesignDepartment = userData?.departments?.some(
    (d) => d.id === SKETCH_DESIGN_DEPARTMENT_ID
  );

  const canOvertimeApproval = userData && hasAccessToOvertimeApproval(userData);
  const canTimesheetApproval = userData && hasAccessToTimesheetApproval(userData);
  const canSalaryApproval = userData && hasAccessToSalaryApproval(userData);
  const canProjectApproval = userData && hasAccessToProjectApproval(userData);
  const canEpApproval = userData && hasAccessToEpApproval(userData);
  const canApprovals = userData && hasAccessToApprovals(userData);

  const navItems = [
    {
      label: "ПОРТФЕЛЬ ПРОЕКТОВ",
      path: "/projects/list",
      submenu: [
        { label: "Список проектов", path: "/projects/list" },
        hasAdminAccess && { label: "Создание проекта", path: "/projects/add" },
        hasAdminAccess && {
          label: "Редактирование проекта",
          path: "/projects/edit",
        },
        hasAdminAccess && {
          label: "Редактирование команды проекта",
          path: "/projects/teams",
        },
        hasProjectFinanceAccess && {
          label: "Финансы проекта",
          path: "/projects/finance",
        },
        hasLaborAnalysisAccess && {
          label: "Анализ трудозатрат",
          path: "/projects/labor-analysis",
        },
        hasCounterpartiesAccess && {
          label: "База данных контрагентов",
          path: "/projects/counterparties",
        },
        hasPaymentsProgressAccess && {
          label: "Отклонения ЭП",
          path: "/projects/sketch-design/ep-deviations",
        },
      ].filter(Boolean),
    },
    {
      label: "ОТЧЕТЫ",
      path: "/report/new",
      submenu: [
        { label: "Создать новый", path: "/report/new" },
        { label: "Редактировать", path: "/report/edit" },
        !isSketchDesignDepartment && { label: "Табель", path: "/report/all" },
        (userData?.access_levels?.some((level) =>
          ["АДМС", "НО", "РО", "РГ", "ФИН", "ИК", "АДМП"].includes(
            getLevelName(level),
          )
        ) ||
          userData?.id === 167 ||
          userData?.id === 229 ||
          userData?.id === 30 ||
          userData?.id === 275 ||
          userData?.id === 131) && {
          label: "Табель отдела",
          path: "/report/department",
        },
        userData?.access_levels?.some((level) =>
          ["АДМС", "ИК"].includes(getLevelName(level))
        ) && { label: "Кадровые операции", path: "/report/vacations-sick" },
        userData?.access_levels?.some((level) => getLevelName(level) === "АДМС") && {
          label: "Удаление отгулов",
          path: "/report/dayoff-deletion",
        },
        hasPaymentsProgressAccess && {
          label: "Прогресс выплат",
          path: "/report/payments-progress",
        },
      ].filter(Boolean),
    },
    canApprovals && {
      label: (
        <div style={{ position: "relative", display: "inline-block" }}>
          СОГЛАСОВАНИЯ
          {totalPendingCount > 0 && <Badge count={totalPendingCount} />}
        </div>
      ),
      path: canOvertimeApproval
        ? "/approvals/overtime-approval"
        : canTimesheetApproval
        ? "/approvals/timesheet-approval"
        : canSalaryApproval
        ? "/approvals/salary-approval"
        : canProjectApproval
        ? "/approvals/project-approval"
        : "/approvals/sketch-design-approval",
      submenu: [
        canOvertimeApproval && {
          label: (
            <div style={{ position: "relative", display: "inline-block" }}>
              Согласование переработок
              {pendingOvertimesCount > 0 && (
                <Badge count={pendingOvertimesCount} />
              )}
            </div>
          ),
          path: "/approvals/overtime-approval",
        },
        canOvertimeApproval && {
          label: (
            <div style={{ position: "relative", display: "inline-block" }}>
              Согласование отгулов
              {pendingDayoffsCount > 0 && <Badge count={pendingDayoffsCount} />}
            </div>
          ),
          path: "/approvals/dayoff-approval",
        },
        canOvertimeApproval && {
          label: "История согласований",
          path: "/approvals/approved-reports",
        },
        canTimesheetApproval && {
          label: (
            <div style={{ position: "relative", display: "inline-block" }}>
              Согласование табелей
              {pendingTimesheetsCount > 0 && (
                <Badge count={pendingTimesheetsCount} />
              )}
            </div>
          ),
          path: "/approvals/timesheet-approval",
        },
        canSalaryApproval && {
          label: (
            <div style={{ position: "relative", display: "inline-block" }}>
              Согласование зарплаты
              {pendingSalaryApprovalsCount > 0 && (
                <Badge count={pendingSalaryApprovalsCount} />
              )}
            </div>
          ),
          path: "/approvals/salary-approval",
        },
        canEpApproval && {
          label: (
            <div style={{ position: "relative", display: "inline-block" }}>
              Согласование проектов ЭП
              {pendingSketchDesignApprovalsCount > 0 && (
                <Badge count={pendingSketchDesignApprovalsCount} />
              )}
            </div>
          ),
          path: "/approvals/sketch-design-approval",
        },
        canEpApproval && {
          label: (
            <div style={{ position: "relative", display: "inline-block" }}>
              Согласование выплат ЭП
              {pendingPaymentsProgressApprovalsCount > 0 && (
                <Badge count={pendingPaymentsProgressApprovalsCount} />
              )}
            </div>
          ),
          path: "/approvals/payments-progress-approval",
        },
        canProjectApproval && {
          label: (
            <div style={{ position: "relative", display: "inline-block" }}>
              Согласование проекта
              {pendingAgreementApprovalsCount > 0 && (
                <Badge count={pendingAgreementApprovalsCount} />
              )}
            </div>
          ),
          path: "/approvals/project-approval",
        },
      ].filter(Boolean),
    },
    {
      label: "КАЛЕНДАРЬ",
      path: "/calendar-v2",
    },
    hasKnowledgeBaseAccess && {
      label: "БАЗА ЗНАНИЙ",
      path: "/knowledge-base",
    },
    hasOrgChartAccess && {
      label: "ОРГСХЕМА",
      path: "/orgchart-new",
    },
  ].filter(Boolean);

  if (hasFinancialReportAccess) {
    navItems.push({
      label: "ОТЧЕТ БУХГАЛТЕРА",
      path: "/financial-report/employee",
      submenu: [
        { label: "Отчет по сотрудникам", path: "/financial-report/employee" },
        { label: "Расчетные листы", path: "/financial-report/payslip" },
        { label: "Заработная плата", path: "/financial-report/salary" },
      ],
    });
  }

  navItems.push({
    label: userName,
    path: "/account",
    submenu: [
      { label: "Личная карточка", path: "/account/employee-card" },
      { label: "Переработки и отгулы", path: "/account/overtime" },
      { label: "Расчетный лист", path: "/account/new-payslip" },
      { label: "Дисциплина", path: "/account/discipline" },
      hasUserAdminAccess && {
        label: "Администрирование пользователей",
        path: "/account/admin/register",
      },
      { label: "Выйти", path: "/account/logout" },
    ].filter(Boolean),
  });

  return (
    <div className="App">
      {location.pathname !== "/signin" && <Header navItems={navItems} />}
      <Routes>
        <Route path="/" element={<Main />} />
        <Route path="/projects/list" element={<ProjectsList />} />
        <Route path="/account/*" element={<Account />} />
        <Route path="/report/*" element={<Report />} />
        <Route path="/approvals/*" element={<Approvals />} />
        <Route path="/news" element={<AllNews />} />
        <Route
          path="/signin"
          element={
            <Auth>
              <Login />
            </Auth>
          }
        />
        {hasAdminAccess && (
          <>
            <Route path="/projects/add" element={<NewProject />} />
            <Route path="/projects/edit" element={<EditProjectSelect />} />
            <Route path="/projects/edit/:id" element={<EditProject />} />
            <Route path="/projects/teams" element={<AddProjectTeamSelect />} />
            <Route path="/projects/teams/:id" element={<AddProjectTeam />} />
          </>
        )}
        {hasProjectFinanceAccess && (
          <>
            <Route
              path="/projects/finance"
              element={<ProjectFinanceSelect />}
            />
            <Route path="/projects/finance/:id" element={<ProjectFinance />} />
            <Route
              path="/projects/sketch-design/finance/:id"
              element={<ProjectFinanceSketchDesign />}
            />
          </>
        )}
        {hasLaborAnalysisAccess && (
          <>
            <Route
              path="/projects/labor-analysis"
              element={<LaborAnalysisSelect />}
            />
            <Route
              path="/projects/labor-analysis/:id"
              element={<LaborAnalysis />}
            />
          </>
        )}
        {hasPaymentsProgressAccess && (
          <Route
            path="/projects/sketch-design/ep-deviations"
            element={<EpAccumulatedDeviations />}
          />
        )}
        {hasCounterpartiesAccess && (
          <>
            <Route
              path="/projects/counterparties"
              element={<CounterpartiesDatabase />}
            />
            <Route
              path="/projects/counterparties/add"
              element={<AddCounterparty />}
            />
            <Route
              path="/projects/counterparties/edit/:id"
              element={<AddCounterparty />}
            />
            <Route
              path="/projects/counterparties/:id"
              element={<CounterpartyDetails />}
            />
          </>
        )}
        {hasFinancialReportAccess && (
          <Route path="/financial-report/*" element={<FinancialReportTabs />} />
        )}
        <Route path="/stats" element={<Stats />} />
        {hasOrgChartAccess && (
          <Route path="/orgchart-new" element={<OrgChartNew />} />
        )}
        <Route path="/my-department" element={<Departments />} />
        <Route path="/calendar-v2" element={<MainCalendarV2 />} />
        <Route path="/onboarding-materials" element={<OnboardingMaterials />} />
        <Route path="/vacation-schedule" element={<VacationSchedule />} />
        {hasKnowledgeBaseAccess && (
          <>
            <Route path="/knowledge-base" element={<KnowledgeBase />} />
            <Route
              path="/knowledge-base/admin"
              element={<KnowledgeBaseAdmin />}
            />
            <Route
              path="/knowledge-base/admin/add-material"
              element={<AddMaterial />}
            />
            <Route
              path="/knowledge-base/organizational-training"
              element={<TrainingList trainingType="organizational" />}
            />
            <Route
              path="/knowledge-base/organizational-training/:courseId/lesson/:lessonId"
              element={<TrainingLesson />}
            />
            <Route
              path="/knowledge-base/organizational-training/:courseId/test"
              element={<OrganizationalTrainingTest />}
            />
            <Route
              path="/knowledge-base/profile-training"
              element={<TrainingList trainingType="profile" />}
            />
            <Route
              path="/knowledge-base/profile-training/:categoryId"
              element={<TrainingList trainingType="profile" />}
            />
            <Route
              path="/knowledge-base/profile-training/:chapterId/:courseId/lesson/:lessonId"
              element={<TrainingLesson />}
            />
          </>
        )}
        <Route
          path="/test-push-notifications"
          element={<PushNotificationTest />}
        />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <>
      <ReactNotifications />
      <div data-environment={process.env.REACT_APP_ENV}>
        <UserProvider>
          <PendingReportsProvider>
            <PendingTimesheetsProvider>
              <PendingSalaryApprovalsProvider>
                <PendingSketchDesignApprovalsProvider>
                  <PendingPaymentsProgressApprovalsProvider>
                    <AppContent />
                  </PendingPaymentsProgressApprovalsProvider>
                </PendingSketchDesignApprovalsProvider>
              </PendingSalaryApprovalsProvider>
            </PendingTimesheetsProvider>
          </PendingReportsProvider>
        </UserProvider>
      </div>
    </>
  );
}

export default App;
